'use client';

/**
 * ObeyaPlanLensDecisionsCard — one entry per selected requirement: the
 * pick, its quantity and line total, and the real candidates it was chosen
 * over, side by side with their tiers. A renderer of shipped facts — no
 * gap, saving or 'cheapest' exists anywhere, so none is drawn.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanLensDecisionsCard
 * @see klyde_edge/klyde_intelligence/projects/plan_lenses.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { PlanLensFrame } from './plan-lens-frame';
import { usePlanLenses, money, TIER_COLOR, type CandidateOption } from './use-plan-lenses';

export function ObeyaPlanLensDecisionsCard() {
  const { contentKey } = useCardIdentity();
  const { mcpId, strategy, d, isLoading, queryKey } = usePlanLenses();

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_lens_decisions',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: d ? d.labels.decisions_title : 'Picks and alternatives',
    json: d ? d.decisions : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy]));

  if (!mcpId) return null;
  if (isLoading && !d) return <div className={cn(text.muted, 'p-6 text-center')}>…</div>;
  if (!d) return null;

  const L = d.labels;
  const option = (o: CandidateOption, strong: boolean, key: string) => (
    <div key={key} className={cn('flex items-center gap-2 py-0.5', !strong && 'opacity-70')}>
      <span className="inline-block w-[8px] h-[8px] rounded-full shrink-0" style={{ background: TIER_COLOR[o.tier] }} />
      <span className={cn(text.body, 'flex-1 truncate', strong && 'font-semibold')}>{o.name ?? L.unlabelled}</span>
      <span className={cn(text.muted, 'whitespace-nowrap')}>{o.vendor}</span>
    </div>
  );

  return (
    <PlanLensFrame title={L.decisions_title} meta={L.decisions_meta}>
      <div className="flex flex-col gap-4">
      {d.decisions.data.selected_requirements.map((r, i) => (
        <div key={`${r.requirement}-${r.pick.name}-${i}`} className="flex gap-6 flex-wrap pb-4 border-b border-border last:border-b-0">
          <div className="w-[220px] shrink-0">
            <div className={cn(text.body, 'font-semibold')}>{r.requirement ?? L.unlabelled}</div>
            <div className={cn(text.muted)}>
              {L.col_qty} <b className="text-foreground tabular-nums">{r.quantity ?? L.mark_none}</b> · {L.line_total} <b className="text-foreground tabular-nums">{r.pick_line_total != null ? money(r.pick_line_total) : L.mark_none}</b>
            </div>
            <div className={cn(text.muted)}>{L.serves} {r.rooms.join(', ')}</div>
          </div>
          <div className="flex-1 min-w-[360px]">
            <div className={cn(text.eyebrow, 'mb-1')}>{L.col_picked}</div>
            {option(r.pick, true, 'pick')}
            {r.alternatives.length > 0 && <div className={cn(text.eyebrow, 'mt-2 mb-1')}>{L.col_alternatives}</div>}
            {r.alternatives.map((a, j) => option(a, false, `alt-${j}`))}
          </div>
        </div>
      ))}
      </div>
    </PlanLensFrame>
  );
}
